import { isNil } from 'lodash';
import { Queries } from '@shared/api';
import { getSharedApolloClient } from '@shared/api/client';
import { IS_TYPING_TIMEOUT_DURATION } from '@shared/styles';
import { API } from '@shared/types/api';

/*
    This file contains logic to remove typing users X seconds after they've
    last typed. This logic is handled by the client; the server just pushes
    isTyping WS updates whenever a user types something.

    Examples:
     1. A types => waits for 5 seconds => A should no longer be seen as typing
     2. A types => waits for 2 seconds => A types => waits for 3 seconds => A should still be typing
*/

export class MBUserTypingRemovalScheduler {
  // { [channel.slug]: { [user.id]: timeout } }
  timeouts: Dict<Dict<number /* timeout id */, number /* user id */>> = {};

  get apolloClient() {
    return getSharedApolloClient();
  }

  clear(user: API.ShortUserFragment, channelSlug: string) {
    if (!isNil(this.timeouts[channelSlug]?.[user.id])) {
      clearTimeout(this.timeouts[channelSlug][user.id]);
      this.timeouts[channelSlug][user.id] = undefined;
    }
  }

  schedule(user: API.ShortUserFragment, channelSlug: string) {
    this.clear(user, channelSlug);

    const timeout = setTimeout(
      this.remove,
      IS_TYPING_TIMEOUT_DURATION,
      // these two parameters will be sent to this.remove
      // https://stackoverflow.com/a/13148281/9117995
      user,
      channelSlug,
    );

    // Store the timeout id from memory
    if (!this.timeouts[channelSlug]) {
      this.timeouts[channelSlug] = {};
    }
    this.timeouts[channelSlug][user.id] = timeout;
  }

  // Note: we set remove as prop on the class (instead of function)
  // so that we can access "this" from the class' context instead of
  // the caller's context
  remove = (user: API.ShortUserFragment, channelSlug: string) => {
    this.clear(user, channelSlug);

    const data = this.apolloClient.readQuery<API.ChannelChatThreadQuery>({
      query: Queries.channel.chatThread,
      variables: { slug: channelSlug },
    });

    if (!data || !data.channel) {
      return;
    }

    this.apolloClient.writeQuery({
      data: {
        ...data,
        channel: {
          ...data.channel,
          isTyping: data.channel.isTyping.filter(u => u?.id !== user.id),
        },
      },
      query: Queries.channel.chatThread,
      variables: { slug: channelSlug },
    });
  };
}
